import * as React from "react";
import { ChevronRight } from "lucide-react";
import type { ProductAlertEpisode } from "../../lib/api/products";
import { formatAlertDuration, severityColor, severityLabel, sortAlertsOpenFirst } from "./alertDisplay";
import { PulsingDot } from "./PulsingDot";
import * as s from "./AlertSummaryCard.css";

/** The Timeline's row/bar id — present when rows come from a Timeline (all
 *  real callers), absent for the one-off badge tooltip use (ActiveAlertsCountBadge),
 *  which has no Timeline selection/hover state to match against. */
type AlertSummaryRow = ProductAlertEpisode & { id?: string };

/** 15%-alpha accent behind an open row — reads on both light and dark card backgrounds. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const [, r, g, b] = m;
  return `rgba(${parseInt(r!, 16)}, ${parseInt(g!, 16)}, ${parseInt(b!, 16)}, ${alpha})`;
}

/**
 * One line per alert (bold name+code, severity label, duration, ongoing/closed
 * styling) — the single renderer shared by the Timeline's hover-card and the
 * "N active alerts" badge tooltip. Do not duplicate this line-rendering logic
 * elsewhere.
 */
export function AlertSummaryCard({
  alerts,
  emptyLabel = "No alerts",
  /** Timestamp to treat as "now" for open-alert duration — pass the Timeline
   *  cursor time when rendering a historical hover-card; omit for live views. */
  asOf,
  /**
   * Color per alert. Defaults to severity-based (matches the "N active
   * alerts" badge, whose color also comes from severity). The Timeline
   * passes a type-based color here instead, so each line's dot/text matches
   * the color of the bar it's summarizing.
   */
  getColor = (alert) => severityColor(alert.severity).fg,
  highlightedId,
  selectedId,
}: {
  alerts: AlertSummaryRow[];
  emptyLabel?: string;
  asOf?: number;
  getColor?: (alert: ProductAlertEpisode) => string;
  /** The row currently under the mouse in the Timeline chart — highlights the
   *  matching line here so the tooltip and chart hover stay in sync. */
  highlightedId?: string | null;
  /** The row currently shown in the alert context panel — marked with a
   *  trailing chevron so it's clear which line the panel is displaying. */
  selectedId?: string | null;
}) {
  if (alerts.length === 0) {
    return <div className={s.empty}>{emptyLabel}</div>;
  }

  const sorted = sortAlertsOpenFirst(alerts);
  const nowMs = asOf ?? Date.now();

  return (
    <div className={s.list}>
      {sorted.map((alert) => {
        const isOpen = alert.active;
        const color = getColor(alert);
        const openedMs = new Date(alert.openedAt).getTime();
        const closedMs = isOpen ? nowMs : new Date(alert.closedAt ?? alert.openedAt).getTime();
        const isHighlighted = alert.id != null && alert.id === highlightedId;
        const isSelected = alert.id != null && alert.id === selectedId;

        return (
          <div key={`${alert.code}_${alert.openedAt}`} className={s.row}>
            {isHighlighted && <div className={s.rowHighlight} style={{ backgroundColor: hexToRgba(color, 0.15) }} />}
            <PulsingDot color={color} active={isOpen} />
            <span className={[s.text, isOpen ? s.textActive : null].filter(Boolean).join(" ")}>
              <span className={isOpen ? s.nameCodeActive : s.nameCode}>
                {alert.name} ({alert.code})
              </span>
              {" - "}
              {severityLabel(alert.severity)}
              {" - "}
              {formatAlertDuration(openedMs, closedMs)}
              {" - "}
              {isOpen ? "ongoing" : "closed"}
            </span>
            {isSelected && <ChevronRight size={12} className={s.selectedChevron} aria-hidden />}
          </div>
        );
      })}
    </div>
  );
}
